<template>
    <section class="qp-card p-4 sm:p-6">
        <HeadingComp :text="$t('Earnings history')" :subtitle="$t('Stock returns after past reports')" />
        <div v-if="loading" class="h-[160px] flex items-center justify-center"><Loading /></div>
        <template v-else>
            <table class="w-full text-[13px]">
                <thead>
                    <tr class="border-b border-line-card">
                        <th class="qp-label text-left font-semibold pb-2">{{ $t('Report date') }}</th>
                        <th v-for="h in horizons" :key="h" class="qp-label text-right font-semibold pb-2 pl-2">
                            {{ $t('{n}D', { n: h }) }}
                        </th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-line-soft">
                    <tr v-for="r in visibleRows" :key="r.date">
                        <td class="py-2 text-ink-2 whitespace-nowrap">
                            {{ fmt.date(r.date) }}<span v-if="!r.hourKnown" class="text-faint"
                                :title="$t('earnings-hour-unknown')"> *</span>
                        </td>
                        <td v-for="(v, j) in r.returns" :key="j" class="qp-num py-2 pl-2 text-right font-medium"
                            :class="toneText(v)">{{ pctText(v) }}</td>
                    </tr>
                </tbody>
            </table>
            <button v-if="rows.length > previewCount" type="button" @click="expanded = !expanded"
                class="mt-2 text-[13px] font-medium text-brand hover:underline">
                {{ expanded ? $t('Show fewer') : $t('Show all {n}', { n: rows.length }) }}
            </button>

            <h3 class="qp-label mt-5 mb-1">{{ $t('Summary') }} · {{ $t('{n} reports', { n: rows.length }) }}</h3>
            <table class="w-full text-[13px]">
                <tbody class="divide-y divide-line-soft">
                    <tr v-for="s in summaryRows" :key="s.label">
                        <td class="py-2 text-muted">{{ s.label }}</td>
                        <td v-for="(v, j) in s.values" :key="j" class="qp-num py-2 pl-2 text-right font-medium"
                            :class="s.count ? 'text-ink' : toneText(v)">
                            {{ s.count ? v : pctText(v) }}
                        </td>
                    </tr>
                </tbody>
            </table>

            <p class="text-[11px] leading-[1.5] text-faint mt-3">
                {{ $t('earnings-history-method') }}
                <span v-if="hasUnknownHour"> * {{ $t('earnings-hour-unknown') }}</span>
            </p>
        </template>
    </section>
</template>

<script>
import HeadingComp from './HeadingComp.vue'
import Loading from '@/components/loading/Loading.vue'
import { formatMixin, DASH } from '@/utils/format'
import { HORIZONS, earningsSummary } from '@/utils/earningsHistory'

export default {
    name: 'EarningsHistory',
    mixins: [formatMixin],
    components: { HeadingComp, Loading },
    props: {
        // earningsReturns() rows, newest first.
        rows: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
    },
    data() {
        return { expanded: false, previewCount: 8, horizons: HORIZONS }
    },
    computed: {
        visibleRows() {
            return this.expanded ? this.rows : this.rows.slice(0, this.previewCount)
        },
        hasUnknownHour() {
            return this.visibleRows.some((r) => !r.hourKnown)
        },
        summaryRows() {
            const s = earningsSummary(this.rows)
            const pick = (key) => s.map((h) => h[key])
            return [
                { label: this.$t('# Positive'), values: pick('positive'), count: true },
                { label: this.$t('# Negative'), values: pick('negative'), count: true },
                { label: this.$t('Median positive'), values: pick('medianPositive') },
                { label: this.$t('Median negative'), values: pick('medianNegative') },
                { label: this.$t('Max positive'), values: pick('maxPositive') },
                { label: this.$t('Max negative'), values: pick('maxNegative') },
            ]
        },
    },
    watch: {
        // A new symbol starts collapsed.
        rows() {
            this.expanded = false
        },
    },
    methods: {
        // Rounded to the displayed 0.1 so -0.03% reads as a neutral 0.0%, not a red -0.0%.
        shown(v) {
            return v === null || v === undefined ? null : Math.round(v * 10) / 10 || 0
        },
        pctText(v) {
            return v === null || v === undefined ? DASH : this.fmt.pct(this.shown(v), 1, { signed: true })
        },
        toneText(v) {
            const t = this.fmt.tone(this.shown(v))
            if (t === 'up') return 'text-up'
            if (t === 'down') return 'text-down'
            return 'text-muted'
        },
    },
}
</script>
